/* ===========================================================
   Kevin Murphy — Modern Minimal Product Shortcodes
   Shortcodes:
     [collection-carousel="collection-handle"]
     [product-feature="product-handle"]
     [tip-text="Your tip text..."]

   Data sources (public Shopify endpoints):
     - /collections/{handle}/products.json
     - /products/{handle}.js

   Notes:
   - Uses Swiper.js for carousels
   - Robust to late-injected article content; waits for Swiper
   =========================================================== */

(function () {
  const ARTICLE_SELECTOR = [
    '.article-template',
    '.article-template__content',
    '.article__content',
    '.blog__article__content',
    '.rte',
    '.page-width .rte',
    '.elementor-widget-theme-post-content',
    'rte-formatter',
    '.shopify-policy__container'
  ].join(', ');

  const HAS_SHORTCODE_RE = /\[(?:collection-carousel|product-feature|tip-text)="[^"]+"\]/;

  ready(initWithObserver);

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function initWithObserver() {
    processAll();

    if (document.body && HAS_SHORTCODE_RE.test(document.body.textContent || '')) {
      const mo = new MutationObserver(() => {
        processAll();
        if (!HAS_SHORTCODE_RE.test(document.body.textContent || '')) mo.disconnect();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  function processAll() {
    const containers = document.querySelectorAll(ARTICLE_SELECTOR);

    containers.forEach(container => {
      const html = container.innerHTML;
      if (!HAS_SHORTCODE_RE.test(html)) return;

      let cIdx = 0;
      let replaced = html.replace(/\[collection-carousel="([^"]+)"\]/g, (_, handle) => {
        const id = `km-carousel-${handle}-${cIdx++}`;
        return `
          <section id="${id}" class="km-carousel-wrapper km-carousel" data-collection="${handle}">
            <header class="km-blockhead">
              <div class="km-blockhead__title">Shop</div>
              <div class="km-blockhead__rule" aria-hidden="true"></div>
            </header>

            <div class="km-carousel__inner">
              <div class="swiper-container km-swiper" aria-label="Product carousel">
                <div class="swiper-wrapper"></div>
              </div>
            </div>
          </section>
        `;
      });

      let pIdx = 0;
      replaced = replaced.replace(/\[product-feature="([^"]+)"\]/g, (_, handle) => {
        const id = `km-feature-${handle}-${pIdx++}`;
        return `
          <section id="${id}" class="km-feature-wrapper" data-product="${handle}">
            <div class="km-feature">
              <div class="km-feature__media">
                <a class="km-feature__media-link" href="/products/${handle}" rel="noopener">
                  <div class="km-feature__img km-skel" aria-hidden="true"></div>
                </a>

                <div class="km-feature__cta-row">
                  <a class="km-btn km-btn--outline km-feature__view" href="/products/${handle}" rel="noopener">View product</a>

                  <form class="km-feature__purchase" action="/cart/add" method="post">
                    <input type="hidden" name="id" value="">
                    <button type="submit" class="km-btn km-btn--primary">Add to cart</button>
                  </form>
                </div>
              </div>

              <div class="km-feature__details">
                <div class="km-feature__eyebrow km-skel" aria-hidden="true">—</div>

                <h2 class="km-feature__title km-skel">Loading…</h2>

                <div class="km-feature__desc km-skel"></div>

                <div class="km-feature__meta">
                  <div class="km-feature__price km-skel">$—</div>
                  <div class="km-feature__status" aria-live="polite"></div>
                </div>

                <div class="km-feature__variant-wrap">
                  <div class="km-feature__variant-label">Variant</div>
                  <div class="km-feature__variant-grid" role="listbox" aria-label="Select variant"></div>
                </div>
              </div>
            </div>
          </section>
        `;
      });

      // TIP SHORTCODE
      let tIdx = 0;
      replaced = replaced.replace(/\[tip-text="([^"]+)"\]/g, (_, tipText) => {
        const id = `km-tip-${tIdx++}`;
        const safe = escapeHtml(tipText);
        return `
          <aside id="${id}" class="km-tip" role="note" aria-label="Tip">
            <div class="km-tip__rail" aria-hidden="true"></div>
            <div class="km-tip__body">
              <div class="km-tip__label">Tip</div>
              <div class="km-tip__text">${safe}</div>
            </div>
          </aside>
        `;
      });

      if (replaced !== html) container.innerHTML = replaced;
    });

    buildAll();
  }

  function buildAll() {
    document.querySelectorAll('.km-carousel[data-collection]').forEach(buildCollectionCarousel);
    document.querySelectorAll('.km-feature-wrapper[data-product]').forEach(buildProductFeature);
  }

  function waitForSwiper(cb) {
    if (window.Swiper) return cb();
    const iv = setInterval(() => {
      if (window.Swiper) {
        clearInterval(iv);
        cb();
      }
    }, 50);
    setTimeout(() => clearInterval(iv), 4000);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function stripHtml(str) {
    const div = document.createElement('div');
    div.innerHTML = String(str || '');
    return (div.textContent || div.innerText || '').trim();
  }

  function getCurrencyCode() {
    return (
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      document.querySelector('meta[name="currency"]')?.content ||
      'USD'
    );
  }

  function parseToCents(raw) {
    if (raw == null) return 0;

    if (typeof raw === 'number') {
      return raw >= 1000 ? Math.round(raw) : Math.round(raw * 100);
    }

    const s = String(raw).trim();
    if (/^\d+$/.test(s)) return Math.round(parseInt(s, 10)); // cents
    if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 100); // dollars
    return 0;
  }

  function formatMoney(raw) {
    const cents = parseToCents(raw);

    const moneyFormat =
      (window.theme && (theme.moneyFormat || theme.money_format)) ||
      (window.Shopify && (Shopify.money_format || Shopify.moneyFormat)) ||
      null;

    if (window.Shopify && typeof Shopify.formatMoney === 'function' && moneyFormat) {
      try {
        return Shopify.formatMoney(cents, moneyFormat);
      } catch (e) {}
    }

    const code = getCurrencyCode();
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function getPreferredCollectionImage(images) {
    if (!images || !images.length) return '';
    const first = images[0] && images[0].src;
    const second = images[1] && images[1].src;
    return first || second || '';
  }

  // -------------------------
  // Carousel
  // -------------------------
  function buildCollectionCarousel(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle = wrapper.getAttribute('data-collection');
    const trackEl = wrapper.querySelector('.swiper-wrapper');
    const swiperEl = wrapper.querySelector('.swiper-container');

    fetch(`/collections/${handle}/products.json?limit=20`)
      .then(r => r.json())
      .then(json => {
        const products = json.products || [];
        if (!products.length) {
          wrapper.classList.add('km-empty');
          wrapper.innerHTML = `<div class="km-empty-note">No products found.</div>`;
          return;
        }

        products.forEach(product => {
          const variant = product.variants && product.variants[0];
          if (!variant) return;

          const imgSrc = getPreferredCollectionImage(product.images);
          const title = escapeHtml(product.title || '');
          const href = `/products/${product.handle}`;

          const slide = document.createElement('div');
          slide.className = 'swiper-slide km-slide';
          slide.innerHTML = `
            <article class="km-card">
              <a class="km-card__media" href="${href}" aria-label="View ${title}">
                ${
                  imgSrc
                    ? `<img class="km-card__img" loading="lazy" src="${imgSrc}" alt="${title}">`
                    : `<div class="km-card__img km-skel" aria-hidden="true"></div>`
                }
              </a>

              <div class="km-card__body">
                <div class="km-card__title">${title}</div>

                <div class="km-card__meta">
                  <div class="km-card__price">${formatMoney(variant.price)}</div>
                  <a class="km-btn km-btn--outline km-card__view" href="${href}" aria-label="View ${title}">
                    View
                  </a>
                </div>
              </div>
            </article>
          `;
          trackEl.appendChild(slide);
        });

        waitForSwiper(() => {
          new Swiper(swiperEl, {
            slidesPerView: 1.15,
            spaceBetween: 14,
            loop: false,
            grabCursor: true,
            watchOverflow: true,
            freeMode: { enabled: true, sticky: false },

            breakpoints: {
              520:  { slidesPerView: 2.15, spaceBetween: 14 },
              760:  { slidesPerView: 3.15, spaceBetween: 16 },
              1024: { slidesPerView: 4.15, spaceBetween: 16 },
              1280: { slidesPerView: 5.15, spaceBetween: 18 }
            },

            navigation: false,
            pagination: false
          });
        });
      })
      .catch(err => console.error('KM collection carousel error:', err));
  }

  // -------------------------
  // Product feature
  // -------------------------
  function buildProductFeature(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle = wrapper.getAttribute('data-product');
    const card = wrapper.querySelector('.km-feature');

    const imgBox = card.querySelector('.km-feature__img');
    const titleEl = card.querySelector('.km-feature__title');
    const eyebrowEl = card.querySelector('.km-feature__eyebrow');
    const descEl = card.querySelector('.km-feature__desc');
    const priceEl = card.querySelector('.km-feature__price');
    const statusEl = card.querySelector('.km-feature__status');

    const gridWrap = card.querySelector('.km-feature__variant-wrap');
    const gridEl = card.querySelector('.km-feature__variant-grid');

    const form = card.querySelector('.km-feature__purchase');
    const idInput = form.querySelector('input[name="id"]');
    const submitBtn = form.querySelector('button[type="submit"]');

    const productUrl = `/products/${handle}`;
    card.querySelector('.km-feature__view').href = productUrl;
    card.querySelector('.km-feature__media-link').href = productUrl;

    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(prod => {
        if (!prod || !Array.isArray(prod.variants) || prod.variants.length === 0) {
          titleEl.classList.remove('km-skel');
          titleEl.textContent = 'Product unavailable';
          descEl.classList.remove('km-skel');
          descEl.textContent = 'Please view the product page for details.';
          submitBtn.disabled = true;
          submitBtn.textContent = 'Unavailable';
          return;
        }

        // Image: prefer 2nd then 1st, otherwise featured_image
        const heroImg =
          (Array.isArray(prod.images) && (prod.images[1] || prod.images[0])) ||
          prod.featured_image ||
          '';

        imgBox.classList.remove('km-skel');
        if (heroImg) {
          imgBox.innerHTML = `<img class="km-feature__img-el" loading="lazy" src="${heroImg}" alt="${escapeHtml(prod.title || '')}">`;
        } else {
          imgBox.innerHTML = `<div class="km-feature__img-el km-skel" aria-hidden="true"></div>`;
        }

        // Title + eyebrow
        titleEl.classList.remove('km-skel');
        titleEl.textContent = prod.title || '';

        eyebrowEl.classList.remove('km-skel');
        const eyebrowParts = [];
        if (prod.vendor) eyebrowParts.push(prod.vendor);
        if (prod.type) eyebrowParts.push(prod.type);
        eyebrowEl.textContent = eyebrowParts.join(' · ') || 'Featured';

        // Description (fallback if empty)
        const clean = stripHtml(prod.description || '');
        const short = clean.length > 360 ? clean.slice(0, 357) + '…' : clean;

        descEl.classList.remove('km-skel');
        if (short) {
          descEl.textContent = short;
          descEl.classList.remove('km-feature__desc--fallback');
        } else {
          const tags = String(prod.tags || '')
            .split(',')
            .map(t => t.trim())
            .filter(Boolean);

          const fallback =
            tags.length
              ? `Key notes: ${tags.slice(0, 6).join(' · ')}`
              : 'Tap “View product” to see full details and usage information.';

          descEl.textContent = fallback;
          descEl.classList.add('km-feature__desc--fallback');
        }

        // Variants
        const variants = prod.variants.filter(v => v && v.id);
        const firstAvailable = variants.find(v => v.available) || variants[0];

        priceEl.classList.remove('km-skel');
        priceEl.textContent = firstAvailable ? formatMoney(firstAvailable.price) : formatMoney(0);

        function setStatus(available) {
          statusEl.textContent = available ? 'In stock' : 'Sold out';
          statusEl.classList.toggle('is-soldout', !available);
        }

        if (!firstAvailable) {
          idInput.value = '';
          submitBtn.disabled = true;
          submitBtn.textContent = 'Unavailable';
          setStatus(false);
          return;
        }

        idInput.value = String(firstAvailable.id);
        setStatus(!!firstAvailable.available);

        // Hide selector if single default
        const singleDefault =
          variants.length === 1 &&
          (String(variants[0].title || '').toLowerCase() === 'default title' ||
            String(variants[0].title || '').toLowerCase() === 'default');

        if (singleDefault) {
          if (gridWrap) gridWrap.style.display = 'none';
          wrapper.classList.add('km-feature--compact');
        } else {
          gridEl.innerHTML = '';
          const btns = variants.map(v => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'km-var-btn';
            b.textContent = v.title || 'Variant';
            b.dataset.id = String(v.id);
            b.dataset.price = String(v.price);
            b.dataset.available = v.available ? '1' : '0';
            b.setAttribute('role', 'option');
            b.setAttribute('aria-label', `Select ${v.title || 'Variant'}`);
            if (!v.available) b.setAttribute('aria-disabled', 'true');
            return b;
          });

          btns.forEach(b => gridEl.appendChild(b));

          function selectVariant(btn) {
            btns.forEach(x => x.classList.remove('is-active'));
            btn.classList.add('is-active');

            const available = btn.dataset.available === '1';
            idInput.value = btn.dataset.id;
            priceEl.textContent = formatMoney(btn.dataset.price);

            submitBtn.disabled = !available;
            submitBtn.textContent = available ? 'Add to cart' : 'Sold out';
            setStatus(available);
          }

          btns.forEach(b => b.addEventListener('click', () => selectVariant(b)));

          const initialBtn = btns.find(b => b.dataset.id === String(firstAvailable.id)) || btns[0];
          selectVariant(initialBtn);
        }
      })
      .catch(err => console.error('KM product feature error:', err));
  }
})();
(function () {
  const ARTICLE_SELECTOR = [
    '.article-template',
    '.article-template__content',
    '.article__content',
    '.blog__article__content',
    '.rte',
    '.page-width .rte',
    '.elementor-widget-theme-post-content',
    'rte-formatter',
    '.shopify-policy__container'
  ].join(', ');

  function cleanText(s) {
    return String(s || '').replace(/\s+/g, ' ').trim();
  }

  function labelTables() {
    document.querySelectorAll(`${ARTICLE_SELECTOR} table`).forEach((table) => {
      if (table.__kmLabeled) return;
      table.__kmLabeled = true;

      // Get headers from thead if present, otherwise first row
      let headers = [];
      const thead = table.querySelector('thead');
      if (thead) {
        headers = Array.from(thead.querySelectorAll('th')).map(th => cleanText(th.textContent));
      }

      if (!headers.length) {
        const firstRow = table.querySelector('tr');
        if (firstRow) {
          headers = Array.from(firstRow.children).map(cell => cleanText(cell.textContent));
        }
      }

      if (!headers.length) return;

      // Label each td in tbody rows
      const bodyRows = table.querySelectorAll('tbody tr');
      const rows = bodyRows.length ? bodyRows : table.querySelectorAll('tr');

        rows.forEach((row, rowIndex) => {
        if (!thead && rowIndex === 0) return;

        const tds = Array.from(row.querySelectorAll('td'));
        if (!tds.length) return;

        // ✅ Use first column as the card title on mobile
        const firstHeader = headers[0] || 'Item';
        const firstValue = cleanText(tds[0].textContent);
        if (firstValue) row.setAttribute('data-km-title', `${firstHeader}: ${firstValue}`);

        // Label each td
        tds.forEach((td, i) => {
            if (!td.getAttribute('data-label')) {
            td.setAttribute('data-label', headers[i] || 'Item');
            }
        });
        });
    });
  }

  // Run now + observe for late-loaded content
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelTables);
  } else {
    labelTables();
  }

  const mo = new MutationObserver(() => labelTables());
  mo.observe(document.body, { childList: true, subtree: true });
})();

